// The landing's pointer: a dotted amber ring with a dot at its centre, moving
// together a beat behind the mouse.
//
// Components/Cursor is a different pointer (the dot tracks exactly and only the
// ring trails, at a heavier ease), so this one lives here and that one is left
// alone for the old page.
//
//   • Fine pointers only. On touch nothing mounts and no cursor is hidden.
//   • Over a text field the whole thing fades out and the native caret returns.
//   • Over anything clickable the ring opens up and the dot goes.
//   • Reduced motion: it stays, and follows with no lag.
//   • pointer-events: none, so it never takes a click.

import { useEffect, useRef, useState } from "react";
import { FINE_POINTER, prefersReducedMotion } from "./motion/env";
import "./LandingCursor.css";

// Share of the remaining distance closed each frame, at 60 frames a second.
const EASE = 0.22;
const HOT = 'a, button, label, select, summary, [role="button"], input[type="checkbox"], input[type="radio"]';
const TEXT = 'textarea, input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"])';

export default function LandingCursor() {
    const [enabled, setEnabled] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        if (window.matchMedia?.(FINE_POINTER)?.matches) setEnabled(true);
    }, []);

    useEffect(() => {
        const el = rootRef.current;
        if (!enabled || !el) return undefined;

        const html = document.documentElement;
        html.classList.add("ld-cursor-on");

        const still = prefersReducedMotion();
        const mouse = { x: 0, y: 0 };
        const pos = { x: 0, y: 0 };
        let entered = false;
        let raf = 0;
        let last = 0;

        const place = () => {
            el.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
        };

        const tick = (now) => {
            raf = 0;
            const dt = Math.min((now - last) / 16.67, 4);
            last = now;
            const k = 1 - (1 - EASE) ** dt;
            pos.x += (mouse.x - pos.x) * k;
            pos.y += (mouse.y - pos.y) * k;
            if (Math.abs(mouse.x - pos.x) < 0.1 && Math.abs(mouse.y - pos.y) < 0.1) {
                pos.x = mouse.x;
                pos.y = mouse.y;
                place();
                return;
            }
            place();
            raf = requestAnimationFrame(tick);
        };

        const onMove = (e) => {
            mouse.x = e.clientX;
            mouse.y = e.clientY;
            if (!entered || still) {
                entered = true;
                pos.x = mouse.x;
                pos.y = mouse.y;
                place();
                el.classList.add("is-on");
                return;
            }
            el.classList.add("is-on");
            if (!raf) {
                last = performance.now();
                raf = requestAnimationFrame(tick);
            }
        };

        const onOver = (e) => {
            const t = e.target;
            const text = !!t?.closest?.(TEXT);
            el.classList.toggle("is-text", text);
            el.classList.toggle("is-hot", !text && !!t?.closest?.(HOT));
        };
        const onDown = () => el.classList.add("is-down");
        const onUp = () => el.classList.remove("is-down");
        const onOut = () => el.classList.remove("is-on");

        window.addEventListener("mousemove", onMove, { passive: true });
        window.addEventListener("mouseover", onOver, { passive: true });
        window.addEventListener("mousedown", onDown);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("blur", onOut);
        document.documentElement.addEventListener("mouseleave", onOut);

        return () => {
            cancelAnimationFrame(raf);
            html.classList.remove("ld-cursor-on");
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseover", onOver);
            window.removeEventListener("mousedown", onDown);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("blur", onOut);
            document.documentElement.removeEventListener("mouseleave", onOut);
        };
    }, [enabled]);

    if (!enabled) return null;

    return (
        <div className="ld-cursor" ref={rootRef} aria-hidden="true">
            <span className="ld-cursor__ring" />
            <span className="ld-cursor__dot" />
        </div>
    );
}
